<template>
  <div>
    <!-- Initial Assessment Section -->
    <div class="mb-0">
      <div class="mb-2 mt-1 d-flex align-items-center">
        <h2 class="mb-0">Initial Assessment</h2>
        <b-button
          v-if="productState !== 'draft' && productState !== 'current' && productState !== 'bespoke' && showInitialAssessment"
          variant="outline-secondary"
          size="sm"
          class="ml-auto flex-shrink-0"
          aria-label="Hide Initial Assessment"
          @click="showInitialAssessment = false"
        >
          <feather-icon
            icon="EyeOffIcon"
            size="14"
            class="mr-50"
          />
          Hide
        </b-button>
      </div>

      <!-- Collapsed by default for non-current products -->
      <b-card v-if="productState !== 'draft' && productState !== 'current' && productState !== 'bespoke' && !showInitialAssessment" class="text-center">
        <p class="mb-1">
          This product is <strong>{{ displayProductState }}</strong>.<br>Initial assessment is only shown by default for current products.
        </p>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="showInitialAssessment = true"
        >
          <feather-icon icon="EyeIcon" size="14" class="mr-50" />
          Show Initial Assessment
        </b-button>
      </b-card>

      <template v-if="productState === 'bespoke' || productState === 'draft' || productState === 'current' || showInitialAssessment">
        <b-row class="match-height mb-0">
          <b-col
            cols="12"
            md="6"
          >
            <b-card v-if="figureOne.headings && figureOne.headings.length > 0">
              <h4>Figure 1: Statistics</h4>
              <!--<p>This figure shows some key statistics of the product and compares them to averages of other open products in the market.  Full details of the methodology can be found <a
            :href="`${baseURL}downloadguidepdf.cfm?id=2`"
            target="_blank"
          >here</a></p>
          <p>The overall, price and return scores are expressed on a scale of 0-10 with 10 representing the most favourable. The price score is derived from our estimate of value for money while the return score gives an indication of risk-adjusted return prospects. The overall score is the average of the price and risk scores.</p>
          <p>The riskmap is expressed on a scale of 0 to 10, where 0 represents lowest risk and 10 the highest. It is calculated from contributions from market risk and credit risk which are shown separately. The maturity and duration (expected maturity) are also stated.</p>
          <p><span v-if="keyInfo.rightTbl && keyInfo.rightTbl['Report published']">This report was published on {{ keyInfo.rightTbl["Report published"] }} and compiled in advance with data available to us at that time. It has not been updated since.</span></p>
          -->
              <p>This figure compares key product statistics to market averages. Overall, price, and return scores (0-10 scale, 10 = most favorable) reflect value and risk-adjusted returns. The riskmap (0-10 scale) shows market and credit risk contributions, plus maturity/duration.</p>
              <p><i><b>Note:</b> Published {{ keyInfo.rightTbl['Report published'] }} with data available at that time. Full methodology details available <a :href="`${baseURL}downloadguidepdf.cfm?id=2`" target="_blank">here</a>. Not updated since publication. </i></p>
              <b-table-simple>
                <thead>
                  <tr>
                    <th />
                    <th
                      v-for="(heading, hIndex) in figureOne.headings.slice(1)"
                      :key="hIndex"
                    >
                      {{ heading }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(name, rowIndex) in figureOne.names"
                    :key="rowIndex"
                  >
                    <td><strong>{{ name }}</strong></td>

                    <td
                      v-for="(val, colIndex) in figureOne.values[rowIndex]"
                      :key="colIndex"
                    >
                      {{ val }}
                    </td>
                  </tr>
                </tbody>
              </b-table-simple>
            </b-card>
            <b-card v-else>
              <h4>Figure 1: Statistics</h4>
              <div class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-card>
              <h4>Figure 2: Comparison chart</h4>
              <template v-if="figureThree.datapoints && Object.keys(figureThree.datapoints).length > 0">
                <p class="mb-3">This chart plots the riskmap and overall score for all products on the report centre, which shows all products listed on this site open for investment. <br><br><i><b>Note:</b> The current product is shown with a red diamond marker</i></p>
                <comparison-chart :figure-three="figureThree" />
              </template>
              <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <b-card>
              <h4>Figure 3: Investor results summary</h4>
              <template v-if="figureFour.headings && figureFour.headings.length > 0">
                <p>{{ figureFour.intro }} <br>This table shows a number of mutually exclusive outcomes of product performance classifications and associated statistics.<br><br><i>Note: All figures below are rounded. </i></p>
                <span v-if="figureFour.isCompressed"><br>This product has a large number of auto-call dates. In order to show the information below conveniently we have grouped the call dates (except for the first and last) and show an average investment length and payoff.</span>
                <b-table-simple>
                  <thead>
                    <tr>
                      <th
                        v-for="(heading, hIndex) in figureFour.headings"
                        :key="hIndex"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(name, rowIndex) in figureFour.names"
                      :key="rowIndex"
                    >
                      <td><strong>{{ name }}</strong></td>
                      <td
                        v-for="(val, colIndex) in figureFour.values.map(col => col[rowIndex])"
                        :key="colIndex"
                      >
                        {{ val }}
                      </td>
                    </tr>
                  </tbody>
                </b-table-simple>
              </template>
              <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </template>
    </div>

    <!-- Simulated Product Outcomes - Only show in 'current' state -->
    <template v-if="productState === 'current' || productState === 'draft' || productState === 'bespoke'">
      <h2 class="mb-2 mt-2">Simulated Product Outcomes</h2>
      <div class="mb-0">
        <b-row class="mb-0">
          <b-col cols="12">
            <b-card>
              <h4>Figure 4: Capital</h4>
              <template v-if="figureFive.headings && figureFive.headings.length > 0">
                <p>{{ figureFive.intro }}</p>
                <b-table-simple>
                  <thead>
                    <tr>
                      <th
                        v-for="(heading, hIndex) in figureFive.headings"
                        :key="hIndex"
                      >
                        {{ heading }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(name, rowIndex) in figureFive.names"
                      :key="rowIndex"
                    >
                      <td><strong>{{ name }}</strong></td>
                      <td
                        v-for="(val, colIndex) in figureFive.values.map(col => col[rowIndex])"
                        :key="colIndex"
                      >
                        {{ val }}
                      </td>
                    </tr>
                  </tbody>
                </b-table-simple>
              </template>
              <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="match-height">
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="analysis-card-with-hint">
              <hint-popup
                :id="15"
                class="analysis-hint-popup"
              />
              <h4 class="analysis-title-with-hint">
                Figure 5: Payoff chart
              </h4>
              <template v-if="figureTwo.datapoints && Object.keys(figureTwo.datapoints).length > 0">
                <p>This chart shows a sample of simulated outcomes of the final underlying value and the product payoff for the structured product to help identify the risk and return prospects of the investment.</p>
                <payoff-chart :figure-two="figureTwo" />
              </template>
              <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-card class="analysis-card-with-hint">
              <hint-popup
                :id="16"
                class="analysis-hint-popup"
              />
              <h4 class="analysis-title-with-hint">
                Figure 6: Charts of product outcomes
              </h4>
              <template v-if="figureSix.annualised && Object.keys(figureSix.annualised).length > 0">
                <p>Charts of distribution of structured product returns based on forward looking simulation (market risk only).</p>
                <outcomes-charts :figure-six="figureSix" />
              </template>
              <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                <span>No data available</span>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </template>

    <!-- Live Product Outcomes - Show in 'early_live' and 'live' states -->
    <template v-if="productState === 'early_live' || productState === 'live'">
      <h2 class="mb-2 mt-2">Live Product Outcomes</h2>
      <div class="mb-0">
        <!-- Message for early_live state -->
        <template v-if="productState === 'early_live'">
          <b-card class="mb-2">
            <div class="text-center py-3">
              <p class="mb-0">This product has recently closed for investment</p>
              <p class="mb-1">Live product outcomes will be available after the strike date</p>
            </div>
          </b-card>
        </template>

        <!-- Charts for live state -->
        <template v-if="productState === 'live'">
          <b-row
            v-if="hasLifecycleData"
            class="mb-0"
          >
            <b-col cols="12">
              <b-card>
                <h4>Figure 4: Capital</h4>
                <template v-if="capitalTableData.length > 0">
                  <p>Current probabilities of capital outcomes at maturity</p>
                  <b-table-simple>
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th>Probability (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in capitalTableData"
                        :key="index"
                      >
                        <td><strong>{{ row.name }}</strong></td>
                        <td>{{ row.probability }}</td>
                      </tr>
                    </tbody>
                  </b-table-simple>
                </template>
                <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                  <span>No data available</span>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row
            v-if="hasLifecycleData"
            class="match-height"
          >
            <b-col
              cols="12"
              md="6"
            >
              <b-card>
                <h4>Figure 5: Payoff chart</h4>
                <template v-if="payoffChartData.SHOWDATA && payoffChartData.SHOWDATA.length > 0">
                  <p>Chart of payoff versus underlying</p>
                  <live-payoff-profile :payoff-chart-data="payoffChartData" />
                </template>
                <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                  <span>No data available</span>
                </div>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <b-card class="analysis-card-with-hint">
                <hint-popup
                  :id="16"
                  class="analysis-hint-popup"
                />
                <h4 class="analysis-title-with-hint">
                  Figure 6: Charts of product outcomes
                </h4>
                <template v-if="returnChartAbs.SHOWDATA && returnChartAbs.SHOWDATA.length > 0">
                  <p>Current distribution of outcomes and returns at maturity</p>
                  <live-payoff-distribution
                    :return-chart-abs="returnChartAbs"
                    :return-chart-ann="returnChartAnn"
                  />
                </template>
                <div v-else class="d-flex align-items-center justify-content-center py-3 text-muted">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-75" />
                  <span>No data available</span>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <div
            v-else
            class="text-center py-3"
          >
            <p class="text-muted mb-0">There is no live data for this product.</p>
          </div>
        </template>
      </div>
    </template>

    <!-- Always-mounted hidden charts — pre-render regardless of productState for PDF export -->
    <div style="display:none;">
      <comparison-chart ref="comparisonChart" :figure-three="figureThree" />
      <payoff-chart ref="payoffChart" :figure-two="figureTwo" />
      <outcomes-charts ref="outcomesCharts" :figure-six="figureSix" />
      <live-payoff-profile ref="livePayoffProfile" :payoff-chart-data="payoffChartData" />
      <live-payoff-distribution
        ref="livePayoffDistribution"
        :return-chart-abs="returnChartAbs"
        :return-chart-ann="returnChartAnn"
      />
    </div>

    <!-- Product Outcomes - Show in 'matured' state -->
    <template v-if="productState === 'matured'">
      <h2 class="mb-2 mt-2">Product Outcomes</h2>
      <div class="mb-0">
        <b-card>
          <div v-if="formattedTerminationDate !== ''" class="text-center py-3">
            <p class="text-muted mb-0">This product reached maturity on {{ formattedTerminationDate }}.</p>
            <p class="text-muted mb-0">Outcome tracking is no longer available.</p>
          </div>
          <div v-else class="text-center py-3">
            <p class="text-muted mb-0">This product has reached maturity.</p>
            <p class="text-muted mb-0">Outcome tracking is no longer available.</p>
          </div>
        </b-card>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTableSimple, BButton,
} from 'bootstrap-vue'
import HintPopup from '@/views/components/HintPopup.vue'
import ComparisonChart from './charts/ComparisonChart.vue'
import PayoffChart from './charts/PayoffChart.vue'
import OutcomesCharts from './charts/OutcomesCharts.vue'
import LivePayoffProfile from './charts/LivePayoffProfile.vue'
import LivePayoffDistribution from './charts/LivePayoffDistribution.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTableSimple,
    BButton,
    HintPopup,
    ComparisonChart,
    PayoffChart,
    OutcomesCharts,
    LivePayoffProfile,
    LivePayoffDistribution,
  },
  props: {
    keyInfo: {
      type: Object,
      default: () => ({
        rightTbl: {},
      }),
    },
    figureOne: {
      type: Object,
      default: () => ({
        headings: [],
        names: [],
        values: [],
      }),
    },
    figureTwo: {
      type: Object,
      default: () => ({
        datapoints: {},
        chartsettings: {},
      }),
    },
    figureThree: {
      type: Object,
      default: () => ({
        datapoints: {},
        thisproductdatapoint: {},
      }),
    },
    figureFour: {
      type: Object,
      default: () => ({
        intro: '',
        isCompressed: false,
        headings: [],
        names: [],
        values: [],
      }),
    },
    figureFive: {
      type: Object,
      default: () => ({
        intro: '',
        headings: [],
        names: [],
        values: [],
      }),
    },
    figureSix: {
      type: Object,
      default: () => ({
        annualised: {},
        payoff: {},
      }),
    },
    payoffChartData: {
      type: Object,
      default: () => ({
        SHOWDATA: [],
      }),
    },
    hasLifecycleData: {
      type: Boolean,
      default: false,
    },
    productState: {
      type: String,
      default: 'current',
    },
    returnChartAbs: {
      type: Object,
      default: () => ({
        XAXIS: [],
        SHOWDATA: [],
      }),
    },
    returnChartAnn: {
      type: Object,
      default: () => ({
        XAXIS: [],
        SHOWDATA: [],
      }),
    },
    capitalData: {
      type: Object,
      default: () => ({
        SHOWDATA: {
          DATA: [],
        },
      }),
    },
    terminationDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      showInitialAssessment: false,
    }
  },
  computed: {
    displayProductState() {
      if (this.productState === 'early_live' || this.productState === 'live') return 'live'
      if (this.productState === 'matured') return 'matured'
      return this.productState
    },
    formattedTerminationDate() {
      if (!this.terminationDate) return ''
      const date = new Date(this.terminationDate)
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    },
    capitalTableData() {
      if (!this.capitalData || !this.capitalData.SHOWDATA || !this.capitalData.SHOWDATA.DATA) {
        return []
      }

      const series = this.capitalData.SHOWDATA.DATA
      const tableData = []

      series.forEach(seriesItem => {
        if (seriesItem.data && seriesItem.data.length > 0) {
          // Get the latest data point (last item in the array)
          const latestDataPoint = seriesItem.data[seriesItem.data.length - 1]
          tableData.push({
            name: seriesItem.name || 'Unknown',
            probability: latestDataPoint.y ? parseFloat(latestDataPoint.y).toFixed(2) : '0.00',
          })
        }
      })

      return tableData
    },
  },
  methods: {
    collectCharts() {
      const charts = {}

      const addChart = (ref, id) => {
        const component = this.$refs[ref]
        if (!component) return
        const dataURL = component.getDataURL && component.getDataURL()
        if (!dataURL) return
        charts[id] = dataURL
      }

      addChart('comparisonChart', 'scatterChart')
      addChart('payoffChart', 'payoffLineChart')
      addChart('livePayoffProfile', 'livePayoffChart')
      // Live fig 6: capture both tabs directly from cache
      const liveDistComponent = this.$refs.livePayoffDistribution
      if (liveDistComponent && liveDistComponent.getBothDataURLs) {
        const { outcomes, returns } = liveDistComponent.getBothDataURLs()
        if (outcomes) charts.liveAnnChart = outcomes
        if (returns) charts.liveReturnsChart = returns
      }

      // outcomesCharts only renders for 'current' products — capture both tabs
      const outcomesComponent = this.$refs.outcomesCharts
      if (outcomesComponent && outcomesComponent.getBothDataURLs) {
        const { annualised, payoff } = outcomesComponent.getBothDataURLs()
        if (annualised) charts.annualisedChart = annualised
        if (payoff) charts.payoffBarChart = payoff
      }

      // For live products, livePayoffDistribution serves as annualisedChart
      if (!charts.annualisedChart && charts.liveAnnChart) {
        charts.annualisedChart = charts.liveAnnChart
      }

      return charts
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/chart-apex.scss';
</style>

<style lang="scss" scoped>
.analysis-card-with-hint ::v-deep .card-body {
  position: relative;
}

.analysis-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.analysis-title-with-hint {
  padding-right: 2.5rem;
}

.card-min-height {
  min-height: 460px;
}

.header-card {
  margin-bottom: calc(2rem + 1px) !important;
}
</style>
